import { LEVEL4CATEGORYID } from '@/constant/common'
import Minicard from '@/components/minicard/Minicard.vue'
import VideoPlayer from '@/components/video-player/VideoPlayer.vue'
import { divide, isEmpty, isString, round, toNumber, isArray } from 'lodash'
import Clipboard from 'clipboard'
import qs from 'query-string'
import {
  createApp,
  createVNode,
  getCurrentInstance,
  mergeProps,
  reactive
} from 'vue'
import { isInWorkChat } from './native'
import kwimage from 'kw-image'
import { cookies } from './cookie'
import { getMinicode } from '@/api/common'
import { urlPrefixes } from '@/constant/urlPrefixes'
import { ShareSpuType } from '@/constant/share'

export const baseRootValue = 75

export function setLoading(isShow = true) {
  const loadDom: HTMLElement | null = document.querySelector(
    '#loadingComponent'
  ) as HTMLElement
  if (loadDom) {
    if (isShow) {
      loadDom.style.display = 'flex'
    } else {
      loadDom.style.display = 'none'
    }
  }
}

export function toLogin(url?: string) {
  const referUrl =
    url || (isInFrame() ? window.parent.location.href : location.href)
  if (isInWorkChat()) {
    if (isInFrame()) {
      window.parent.location.href = `https://shequn.cekid.com/workchat-login/login?href=${encodeURIComponent(
        referUrl
      )}`
    } else {
      location.href = `https://shequn.cekid.com/workchat-login/login?href=${encodeURIComponent(
        referUrl
      )}`
    }
  } else {
    location.href = `https://passport.cekid.com/passport/login?cmd=login&referer=${encodeURIComponent(
      referUrl
    )}`
  }
}

/* 跳转门店选择页，back=2关闭webview，back=3重定向回referer地址 */
// https://code.haiziwang.com/digitalize/web/rkhy/rkhy-store/blob/master/src/views/StoreSelector.vue
export function toSelectStore(url?: string, back = 3) {
  const referUrl = url || location.href
  const refererObj = qs.parseUrl(referUrl)
  refererObj.query.entityId = '%entityId%'
  refererObj.query.entityName = '%entityName%'
  const refererStr = qs.stringifyUrl(refererObj, {
    encode: false
  })
  location.href = `https://w.cekid.com/rkhy/rkhy-store/store-selector?back=${back}&referer=${encodeURIComponent(
    `${refererStr}`
  )}`
}

function supportWebp(): boolean {
  const ele = document.createElement('canvas')
  ele.width = 1
  ele.height = 1
  return (
    ele.toDataURL &&
    ele.toDataURL('image/webp').indexOf('data:image/webp') === 0
  )
}

function optimizeImage(
  value: string,
  width: number | undefined,
  height: number | undefined,
  viewType = '2'
) {
  const suffix = supportWebp() ? 'webp' : 'jpg'
  let imgUrl = `${value}?imageView2/${viewType}`
  imgUrl = imgUrl.replace(/^http(s?):/, '')
  if (width) {
    imgUrl += `/w/${width}`
  }
  if (height) {
    imgUrl += `/h/${height}`
  }
  imgUrl += `/q/80/format/${suffix}`
  return imgUrl
}

export function compressImage(
  value: string,
  width?: number,
  height?: number,
  isTailor = false
) {
  let img = ''
  const isSupportWebp = supportWebp()
  if (value) {
    value = value.replace(/^http(s?):/, '')
    if (isSupportWebp && isTailor) {
      img = `${value}?imageMogr2/crop/${width}x${height}/q/80/format/webp`
    } else if (isSupportWebp && !isTailor) {
      img = optimizeImage(value, width, height)
    } else if (!isSupportWebp && isTailor) {
      img = `${value}?imageMogr2/crop/${width}x${height}/q/80/format/jpg`
    } else {
      img = optimizeImage(value, width, height)
    }
  }
  return img
}

/* 裁剪腾讯云图片 */
export function cropImage(value: string, width: number, height: number) {
  return `${value}?imageMogr2/crop/${width}x${height}`
}

export function getImageBase64(src: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas: HTMLCanvasElement = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = src
    img.onload = function () {
      canvas.width = img.width
      canvas.height = img.height
      ctx && ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL('image/png', 1.0))
    }
    img.onerror = function () {
      reject('load error')
    }
  })
}

const colorThief = new window.ColorThief()

//获取图片色系
export function getImageColorThief(
  src: string
): Promise<{ base64: string; color: string }> {
  return new Promise((resolve, reject) => {
    const canvas: HTMLCanvasElement = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = src
    img.onload = function () {
      canvas.width = img.width
      canvas.height = img.height
      ctx && ctx.drawImage(img, 0, 0)
      const palette = colorThief.getPalette(img, 5)
      const dominantColor = palette[0]
      resolve({
        base64: canvas.toDataURL('image/png', 1.0),
        color: `rgba(${dominantColor.join(',')},0.8)`
      })
    }
    img.onerror = function () {
      reject('load error')
    }
  })
}

export function convertImageBase64ToApp(str = '') {
  return str.replace(/^data:image\/(jpeg|png|gif);base64,/, '')
}

export function convertImageBase64ToFile(str = '', fileName = 'tmpFile.png') {
  const arr = str.split(',')
  const mime = arr[0].match(/:(.*?);/) // 此处得到的为文件类型
  const type = (mime && mime[1]) || '' // 此处得到的为文件类型
  const bstr = atob(arr[1]) // 此处将base64解码
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  // 通过以下方式将以上变量生成文件对象，三个参数分别为文件内容、文件名、文件类型
  return new File([u8arr], fileName, { type })
}

/* 修改base64图片的大小 */
export function changeBase64Size(
  base64: string,
  width: number,
  height: number
): Promise<string> {
  return new Promise(resolve => {
    const canvas: HTMLCanvasElement = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.src = base64
    img.width = width
    img.height = height
    img.onload = function () {
      canvas.width = img.width
      canvas.height = img.height
      ctx && ctx.drawImage(img, 0, 0, img.width, img.height)
      resolve(canvas.toDataURL('image/png', 1.0))
    }
    img.onerror = function () {
      resolve(base64)
    }
  })
}

export function toDemotion(value: string | number, rate = 100) {
  let num = toNumber(value)
  num = isNaN(num) ? 0 : num
  return round(divide(num, rate), 2)
}

export function getYenPrice(price: string | number) {
  let integer = ''
  let decimal = ''
  const p = toDemotion(price).toString()
  const result = p.split('.')
  if (result.length === 2) {
    integer = result[0]
    if (parseInt(result[1]) > 0) {
      decimal = result[1]
    }
  } else {
    if ((price as number) >= 0) {
      integer = p
    }
  }
  let value = `${integer}`
  if (!isEmpty(decimal)) {
    value += `.${decimal}`
  }
  return value
}

export function getDistanceKm(m: number) {
  if (m <= 100) {
    return '0.1'
  } else {
    const km = round(m / 1000, 1).toString()
    if (km.split('.').length == 1) {
      return `${km}.0`
    } else {
      return `${km}`
    }
  }
}

/* 获取分享赚金额范围 "{\"minShareAmount\":0,\"maxShareAmount\":30}" */
export function getShareAmount(spu: ShareSpuType) {
  const ratio = spu.ratio
  let minAmount = 0
  let maxAmount = 0
  try {
    if (isString(ratio)) {
      const shareAmount = JSON.parse(ratio) || {}
      minAmount = shareAmount.minShareAmount || 0
      maxAmount = shareAmount.maxShareAmount || 0
    }
  } catch (error) {
    // continue regardless of error
  }
  return {
    minAmount,
    maxAmount
  }
}

export function isSelfYuesao(level4CategoryId: number) {
  return (
    level4CategoryId == LEVEL4CATEGORYID.SIGNED_YUESAO ||
    level4CategoryId == LEVEL4CATEGORYID.NEW_SIGNED_YUESAO
  )
}

/* rem转响应式后的实际尺寸 */
export function rem2RealPx(rem: number) {
  return rem * parseFloat(document.documentElement.style.fontSize)
}

/* 复制文字到剪贴板 */
export async function copyTextClipboard(text = ''): Promise<boolean> {
  return new Promise(resolve => {
    const button = document.createElement('button')
    const clipboard = new Clipboard(button, {
      text: () => text
    })
    clipboard.on('success', (e: ClipboardJS.Event) => {
      console.log(e)
      resolve(true)
      button.remove()
      clipboard.destroy()
    })
    clipboard.on('error', e => {
      console.error(e)
      resolve(false)
      button.remove()
      clipboard.destroy()
    })
    button.click()
  })
}

/* url拼接kwtarget=blank */
export function getKwtargetUrl(url: string) {
  const urlObj = qs.parseUrl(url)
  urlObj.query.kwtarget = 'blank'
  return qs.stringifyUrl(urlObj)
}

/* url参数替换*/
export function replaceUrlKey(url: string, key: string, value: string) {
  const urlObj = qs.parseUrl(url)
  urlObj.query[key] = value
  return qs.stringifyUrl(urlObj)
}

/* 读取cookie替换字符串中的同名参数值  */
export function replaceStrCookie(str: string, sign = '#') {
  // 只替换每个成对#中间的，单个#不替换
  // 例1: "https://life.cekid.com/v/product/123?entityId=#nodeCode#&uid=#uid#&kwtarget=blank"
  //  =>  "https://life.cekid.com/v/product/123?entityId=8002&uid=1300000139&kwtarget=blank"
  // 例2: "https://life.cekid.com/v/product/123?entityId=#nodeCode#&uid=#uid##id-1"
  //  =>  "https://life.cekid.com/v/product/123?entityId=8002&uid=1300000139#id-1"
  const strSplits = str.split(sign)
  let result = ''
  strSplits.forEach((item, index) => {
    // split后，非最后一个的奇数索引是待替换的值
    if (index % 2 == 0) {
      result += item
    } else if (index < strSplits.length - 1) {
      result += (item && cookies.get(item)) || ''
    } else {
      result += sign + item
    }
  })
  return result
}

/* 获取带二维码的海报 */
export async function getPosterBase64(
  bgimg: string,
  insuranceId = 0,
  shareLink = '',
  hserecomkey = '',
  isTCQZ = false
): Promise<string> {
  let qrcodeResource = ''
  if (shareLink) {
    const storeCode =
      cookies.get('storeCodeH5') || cookies.get('nodeCode') || ''
    shareLink = shareLink.replace(/#storeCode#/g, storeCode)
    const minicode = await getMinicode(
      shareLink,
      hserecomkey,
      isTCQZ ? 'hzw_tcqz' : 'hzw_mall'
    )
    qrcodeResource = `data:image/png;base64,${minicode}`
    qrcodeResource = await changeBase64Size(qrcodeResource, 560, 560) // 放大小程序码尺寸，否则当镂空区域比码大的时候，合成出码的位置不对
  } else if (insuranceId > 0) {
    const uid = cookies.get('uid')
    const skey = cookies.get('skey')
    qrcodeResource = `${urlPrefixes.xingbeibaoxianapi}/sharew/item/ercode?itemId=${insuranceId}&uid=${uid}&skey=${skey}`
  }
  try {
    const resultImage = await kwimage.composeImage({
      blankAreaImage: qrcodeResource,
      resourceImage: bgimg,
      format: 'image/jpeg',
      quality: '0.8'
    })
    return resultImage as string
  } catch (e) {
    console.error(e)
    // 可能外面有使用错误信息的必要，抛出
    throw e
  }
}

/* 获取带价格标签的图 */
export async function getMinicardUrl(
  imgUrl: string,
  price?: number
): Promise<string> {
  return new Promise(resolve => {
    const devPreview =
      false /* 开发需要预览时修改为true */ && import.meta.env.DEV
    // eslint-disable-next-line vue/one-component-per-file
    const app = createApp({
      setup() {
        const state = reactive({
          imgUrl,
          price,
          successCallback(data: string) {
            resolve(data)
            !devPreview && minicardInstance.$el.remove()
          },
          failCallback() {
            resolve('')
            !devPreview && minicardInstance.$el.remove()
          },
          devPreview
        })
        return () => createVNode(Minicard, state, null)
      }
    })
    const minicardInstance = app.mount(document.createElement('div'))
    document.body.appendChild(minicardInstance.$el)
    /* 等待图片加载完成调用回调 */
  })
}

/* 视频预览播放（组件的方法调用） */
let videoPlayerInstance: any //组件VideoPlayer.vue的实例
export function videoPreview({ videoUrl = '' }) {
  if (videoPlayerInstance) {
    videoPlayerInstance.open(videoUrl)
    return
  }
  // eslint-disable-next-line vue/one-component-per-file
  const app = createApp({
    setup() {
      const state = reactive({
        show: false,
        videoUrl
      })
      const open = (videoUrl: string) => {
        state.videoUrl = videoUrl
        state.show = true
      }
      const toggle = (show: boolean) => {
        state.show = show
      }
      const currentInstance = getCurrentInstance()
      if (currentInstance) {
        // 给实例暴露外部调用方法
        Object.assign(<any>currentInstance.proxy, {
          open
        })
      }
      return () =>
        createVNode(
          VideoPlayer,
          mergeProps(state, {
            'onUpdate:show': toggle //接收emit('update:show')
          }),
          null
        )
    }
  })
  videoPlayerInstance = app.mount(document.createElement('div'))
  document.body.appendChild(videoPlayerInstance.$el)
  videoPlayerInstance.open()
}

/* 四级品类提取三级品类 */
export function getLevel3CategoryIdBy4(level4CategoryId: number) {
  return Number(`${level4CategoryId}`.slice(0, 6))
}

/* 手机号掩码 */
export function telMask(tel = '') {
  return tel.replace(/(\d{3})\d*(\d{4})/, '$1****$2')
}

/* 延迟执行 */
export function sleep(time: number) {
  return new Promise(resolve => setTimeout(resolve, time))
}

/* 判断是否在iframe中 */
export function isInFrame() {
  return window.parent !== window.self
}

/* 判断设备是否为ios12 */
export function isIos12() {
  const userAgent = navigator.userAgent
  if (/iP(hone|od|ad)/.test(userAgent)) {
    const v = userAgent.match(/OS\s+(\d+)_(\d+)_?(\d+)?/) || []
    return parseInt(v[1], 10) == 12
  } else {
    return false
  }
}

/**
 * 图片尺寸计算 定宽不定高
 */
export const calcImgSize = (
  baseWidth: number,
  srcWidth?: number,
  srcHeight?: number,
  video?: boolean
) => {
  const minRatio = 9 / 16
  const maxRatio = 4 / 3

  let ratio = 0
  if (srcWidth && srcHeight) {
    const tempRatio = srcWidth / srcHeight
    if (video) {
      ratio = tempRatio
    } else if (tempRatio < minRatio) {
      ratio = minRatio
    } else if (tempRatio > maxRatio) {
      ratio = maxRatio
    } else {
      ratio = tempRatio
    }
  } else {
    ratio = maxRatio
  }

  return {
    width: baseWidth,
    height: baseWidth / ratio
  }
}
export function isAndroidsWX() {
  const u = window.navigator.userAgent
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1
  const isWX = /micromessenger/i.test(u)
  return isAndroid && isWX
}

let rootFontSize: number

export function getRootFontSize() {
  if (!rootFontSize) {
    const doc = document.documentElement
    const fontSize = doc.style.fontSize || window.getComputedStyle(doc).fontSize

    rootFontSize = parseFloat(fontSize)
  }

  return rootFontSize
}

export function pxToRemManual(px: number, rootFontSize = baseRootValue) {
  return px / rootFontSize
}

export function removeKeyFromLink(keys: string[] = [], link = location.href) {
  const result = qs.parseUrl(link)
  const query = result.query
  keys.forEach(item => {
    if (Object.keys(query).includes(item)) {
      delete query[item]
    }
  })
  return qs.stringifyUrl(result)
}

export function getShareKeyFromLink() {
  const queryObj = qs.parseUrl(location.href)
  let hserecomkey = ''
  const hsereObj = queryObj.query.hserecomkey
  if (hsereObj) {
    if (isArray(hsereObj) && hsereObj.length > 0) {
      hserecomkey = hsereObj[0] || ''
    } else {
      hserecomkey = hsereObj as string
    }
  }
  return hserecomkey
}

// 适用年龄
export function rangeAge(minMonth = 0, maxMonth = 0, isRecovery = false) {
  //修改显示逻辑
  // 最小月份大于等于0 且 最大月份小于等于24 数据合法 显示产后X月-X月
  // 最小月份等于-1 ||最小月份小于-2 || 最大月份大于24 不显示
  // 最小月份等于0 且 最大月份等于1200 显示 ’不限年龄‘
  /*
    if(minMonth >= 0 && maxMonth <= 24)  {
      return `产后${minMonth}月-${maxMonth}月`
    } else if(minMonth === -2 ) {
      return '不限年龄'
    } else if (minMonth === -1 || minMonth < -2 || maxMonth > 24){
      return null
    }
  */
  if (isRecovery) {
    if (minMonth >= 0 && maxMonth <= 24) {
      return `产后${minMonth}月-${maxMonth}月`
    } else if (minMonth < 0) {
      if (maxMonth > 0) {
        return `孕期-${maxMonth}月`
      } else {
        return '孕期'
      }
    } else {
      return minMonth === 0 && maxMonth === 1200 ? '不限年龄' : null
    }
  } else {
    if (minMonth === 0 && maxMonth === 1200) return '不限年龄'
    // maxMonth = Math.min(144, maxMonth)   //最大月数是144
    const s = Math.floor(minMonth / 12) // 最小岁
    const e = Math.floor(maxMonth / 12) // 最大岁
    let min = ''
    let max = ''
    let temp = ''
    if (minMonth <= -1) {
      min += '孕期'
    } else if (s < 1) {
      min += minMonth == 0 ? minMonth : minMonth + '月'
    } else {
      min += s + '岁'
    }
    temp += min
    if (e < 1) {
      if (maxMonth > 0) {
        max += maxMonth + '月'
      }
    } else {
      max += e + '岁'
    }
    if (min != max && max) {
      temp += '-'
      temp += max
    }
    return temp
  }
}

export function dateFormat(t: number | string, formatter: string) {
  if (!t) {
    return ''
  }
  if (typeof t === 'string') {
    t = t.replace(/-/g, '/')
  }
  const time = new Date(t)
  const year = time.getFullYear()
  const month = `0${time.getMonth() + 1}`.slice(-2)
  const date = `0${time.getDate()}`.slice(-2)
  const hours = `0${time.getHours()}`.slice(-2)
  const minutes = `0${time.getMinutes()}`.slice(-2)
  const seconds = `0${time.getSeconds()}`.slice(-2)
  return formatter
    .replace('YY', year.toString())
    .replace('MM', month)
    .replace('DD', date)
    .replace('hh', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
}

// 把一位数组分成二维数组
export const groupIntoSubArrays = <T>(array: T[], chunkSize: number): T[][] => {
  const result = []
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize)
    result.push(chunk)
  }
  return result
}
